import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { OutboundSchedulePolicy, OutboundSchedulePolicyDay, defaultOutboundSchedulePolicy } from '../../services/outboundAutomation';
import { style_border_default } from '../../../../shared/constants/stylesValues';
import { color_shades_dark, color_shades_darker, color_defaults_white } from '../../../../shared/constants/colors';
import TextField from '../../../../shared/components/forms/TextField';
import CheckboxField from '../../../../shared/components/forms/CheckboxField';
import SingleDayTimeRangeField from '../../../../shared/components/forms/SingleDayTimeRangeField';
import { convert12HourTo24Hour, convert24HourTo12Hour } from '../../../../shared/utils/weeklyScheduleFormatters';
import IconTooltip from '../../../../shared/components/general/IconTooltip';

const infoIcon = require('../../../../shared/content/images/information-circle.svg');

export interface ScheduleBuilderProps {
    value?: OutboundSchedulePolicy;
    onChange: (value: OutboundSchedulePolicy) => void;
    layout?: 'auto' | 'cards' | 'table';
}

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Helper to normalize any time value like "5PM" -> "5:00 PM"
const normalizeTimeWithSpace = (timeStr: string): string => {
    let clean = timeStr.trim().toUpperCase().replace(/\s+/g, '');
    if (!clean.includes(':')) {
        const match = clean.match(/^(\d+)(AM|PM)$/);
        if (match) {
            clean = `${match[1]}:00${match[2]}`;
        }
    }
    return clean.replace(/(AM|PM)$/, ' $1');
};

// Helper: Converts outbound policy timeWindows list ["9:00AM - 11:00AM"] to SingleDayTimeRangeField value "09:00,11:00"
const outboundWindowsToBackendFormat = (windows: string[]): string => {
    if (!windows || windows.length === 0) {
        return '00:00,00:01'; // Represent closed/inactive
    }
    return windows.map(win => {
        const parts = win.split('-').map(s => s.trim().toUpperCase().replace(/\s+/g, ''));
        const start24 = convert12HourTo24Hour(normalizeTimeWithSpace(parts[0] || '9:00AM'));
        const end24 = convert12HourTo24Hour(normalizeTimeWithSpace(parts[1] || '11:00AM'));
        return `${start24},${end24}`;
    }).join('|');
};

// Helper: Converts SingleDayTimeRangeField value "09:00,11:00" back to outbound policy timeWindows list ["9:00AM - 11:00AM"]
const backendFormatToOutboundWindows = (backendStr: string): string[] => {
    if (!backendStr || backendStr === '00:00,00:01') {
        return [];
    }
    return backendStr.split('|').map(p => {
        const [start24, end24] = p.split(',');
        const start12 = convert24HourTo12Hour(start24).replace(/\s+/g, '');
        const end12 = convert24HourTo12Hour(end24).replace(/\s+/g, '');
        return `${start12} - ${end12}`;
    });
};

export const ScheduleBuilder: React.FC<ScheduleBuilderProps> = ({ value, onChange, layout = 'auto' }) => {
    const [policy, setPolicy] = useState<OutboundSchedulePolicy>(value || defaultOutboundSchedulePolicy);
    const [applySundayToAll, setApplySundayToAll] = useState(false);

    useEffect(() => {
        if (value) {
            setPolicy(value);
        }
    }, [value]);

    const handleFieldChange = (dayIndex: number, field: keyof OutboundSchedulePolicyDay, val: any) => {
        let nextApplySunday = applySundayToAll;

        if (dayIndex !== 0) {
            nextApplySunday = false;
        }

        setApplySundayToAll(nextApplySunday);

        const isSundayTransitioningToActive = 
            dayIndex === 0 &&
            field === 'timeWindows' && 
            (policy.dayOfWeekOverrides.find(ov => ov.dayOfWeek === 0)?.timeWindows?.length === 0) && 
            val && val.length > 0;

        const updatedOverrides = policy.dayOfWeekOverrides.map(o => {
            if (isSundayTransitioningToActive) {
                const updatedSunday = {
                    ...o,
                    appointmentsInXDays: 2,
                    timeWindows: ["9:00AM - 11:00AM", "1:00PM - 3:00PM", "5:00PM - 7:00PM"],
                    retryUnconfirmedTimeWindows: ["5PM - 6PM", "7PM - 8PM", "8PM - 9PM"]
                };
                if (nextApplySunday || o.dayOfWeek === 0) {
                    return updatedSunday;
                }
                return o;
            }

            if (dayIndex === 0 && nextApplySunday) {
                if (field === 'appointmentsInXDays') {
                    return { ...o, appointmentsInXDays: val };
                }
                if (field === 'timeWindows') {
                    if (!val || val.length === 0) {
                        return { ...o, timeWindows: [], retryUnconfirmedTimeWindows: [] };
                    }
                    return { ...o, timeWindows: val };
                }
                if (field === 'retryUnconfirmedTimeWindows') {
                    return { ...o, retryUnconfirmedTimeWindows: val };
                }
            }

            if (o.dayOfWeek === dayIndex) {
                if (field === 'timeWindows' && (!val || val.length === 0)) {
                    return { ...o, timeWindows: [], retryUnconfirmedTimeWindows: [] };
                }
                return { ...o, [field]: val };
            }
            return o;
        });

        const newPolicy = {
            dayOfWeekOverrides: updatedOverrides.map(o => ({
                ...o,
                appointmentsInXDays: (typeof o.appointmentsInXDays === 'number' && !isNaN(o.appointmentsInXDays))
                    ? o.appointmentsInXDays
                    : (parseInt(o.appointmentsInXDays as any, 10) || 0)
            }))
        };
        setPolicy(newPolicy);
        onChange(newPolicy);
    };

    const handleDaysAheadChange = (dayIndex: number, rawVal: string) => {
        const parsed = rawVal === '' ? ('' as any) : parseInt(rawVal, 10);
        const finalVal = rawVal === '' ? ('' as any) : (isNaN(parsed) ? 0 : Math.max(0, Math.min(30, parsed)));
        handleFieldChange(dayIndex, 'appointmentsInXDays', finalVal);
    };

    const handleDaysAheadBlur = (dayIndex: number) => {
        const override = getDayOverride(dayIndex);
        if (override && (override.appointmentsInXDays === ('' as any) || isNaN(override.appointmentsInXDays))) {
            handleFieldChange(dayIndex, 'appointmentsInXDays', 0);
        }
    };

    const getDayOverride = (dayIndex: number): OutboundSchedulePolicyDay | undefined => {
        return policy.dayOfWeekOverrides.find(o => o.dayOfWeek === dayIndex);
    };

    const handleToggleApplySunday = (checked: boolean) => {
        setApplySundayToAll(checked);
        if (checked) {
            const sunday = policy.dayOfWeekOverrides.find(o => o.dayOfWeek === 0);
            if (sunday) {
                const updated = policy.dayOfWeekOverrides.map(o => ({
                    ...o,
                    appointmentsInXDays: sunday.appointmentsInXDays,
                    timeWindows: [...sunday.timeWindows],
                    retryUnconfirmedTimeWindows: [...sunday.retryUnconfirmedTimeWindows]
                }));
                const newPolicy = { dayOfWeekOverrides: updated };
                setPolicy(newPolicy);
                onChange(newPolicy);
            }
        }
    };

    const isForcedCards = layout === 'cards';
    const isForcedTable = layout === 'table';

    return (
        <Wrapper>
            {!isForcedCards && (
                <TableWrapper isForcedTable={isForcedTable}>
                    <Table>
                        <thead>
                            <Tr>
                                <Th style={{ width: '90px' }}>Day</Th>
                                <Th style={{ width: '150px' }}>
                                    <HeaderLabelWithTooltip>
                                        Days Before
                                        <IconTooltip text="Call patients this many days before their appointment" place="top" icon={infoIcon} />
                                    </HeaderLabelWithTooltip>
                                </Th>
                                <Th>Primary Time Windows</Th>
                                <Th>Retry Windows</Th>
                            </Tr>
                        </thead>
                        <tbody>
                            {dayNames.map((name, index) => {
                                const override = getDayOverride(index);

                                return (
                                    <React.Fragment key={index}>
                                        <Tr>
                                            <Td style={{ fontWeight: 'bold' }}>{name}</Td>
                                            <Td>
                                                <DaysNumberInput
                                                    name={`daysAhead-${index}`}
                                                    type="number"
                                                    min={0}
                                                    max={30}
                                                    value={override?.appointmentsInXDays !== undefined ? override.appointmentsInXDays : ''}
                                                    placeholder="0"
                                                    onChange={(e) => handleDaysAheadChange(index, e.target.value)}
                                                    onBlur={() => handleDaysAheadBlur(index)}
                                                />
                                            </Td>
                                            <Td>
                                                <SingleDayTimeRangeField
                                                    useCase="outbound"
                                                    disabled={false}
                                                    borderless={true}
                                                    value={outboundWindowsToBackendFormat(override ? override.timeWindows : [])}
                                                    onChange={(backendStr) => handleFieldChange(index, 'timeWindows', backendFormatToOutboundWindows(backendStr))}
                                                />
                                            </Td>
                                            <Td>
                                                <SingleDayTimeRangeField
                                                    useCase="outbound"
                                                    disabled={!override || !override.timeWindows || override.timeWindows.length === 0}
                                                    borderless={true}
                                                    value={outboundWindowsToBackendFormat((override && override.timeWindows && override.timeWindows.length > 0) ? override.retryUnconfirmedTimeWindows : [])}
                                                    onChange={(backendStr) => handleFieldChange(index, 'retryUnconfirmedTimeWindows', backendFormatToOutboundWindows(backendStr))}
                                                />
                                            </Td>
                                        </Tr>
                                        {index === 0 && (
                                            <Tr style={{ backgroundColor: '#fafbfc' }}>
                                                <Td colSpan={4} style={{ padding: '12px 18px', textAlign: 'right' }}>
                                                    <SundayApplyToAllWrapper>
                                                        <CheckboxField
                                                            label="Apply Sunday's schedule to all days"
                                                            checked={applySundayToAll}
                                                            onChange={(e) => handleToggleApplySunday(e.target.checked)}
                                                        />
                                                    </SundayApplyToAllWrapper>
                                                </Td>
                                            </Tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </TableWrapper>
            )}

            {!isForcedTable && (
                <MobileCardList isForcedCards={isForcedCards}>
                    {dayNames.map((name, index) => {
                        const override = getDayOverride(index);

                        return (
                            <MobileDayCard key={index}>
                                <MobileDayHeader>
                                    <span>{name}</span>
                                    {index === 0 && (
                                        <SundayApplyToAllWrapper style={{ marginTop: 0 }}>
                                            <CheckboxField
                                                label="Apply Sunday to all days"
                                                checked={applySundayToAll}
                                                onChange={(e) => handleToggleApplySunday(e.target.checked)}
                                            />
                                        </SundayApplyToAllWrapper>
                                    )}
                                </MobileDayHeader>
                                
                                <MobileInlineFieldRow>
                                    <MobileFieldLabel>
                                        Days Before Appointment
                                        <IconTooltip text="Call patients this many days before their appointment" place="top" icon={infoIcon} />
                                    </MobileFieldLabel>
                                    <DaysNumberInput
                                        name={`daysAhead-mob-${index}`}
                                        type="number"
                                        min={0}
                                        max={30}
                                        value={override?.appointmentsInXDays !== undefined ? override.appointmentsInXDays : ''}
                                        placeholder="0"
                                        onChange={(e) => handleDaysAheadChange(index, e.target.value)}
                                        onBlur={() => handleDaysAheadBlur(index)}
                                    />
                                </MobileInlineFieldRow>

                                <MobileFieldRow>
                                    <MobileFieldLabel>Primary Allowed Calling Windows</MobileFieldLabel>
                                    <SingleDayTimeRangeField
                                        useCase="outbound"
                                        disabled={false}
                                        borderless={true}
                                        value={outboundWindowsToBackendFormat(override ? override.timeWindows : [])}
                                        onChange={(backendStr) => handleFieldChange(index, 'timeWindows', backendFormatToOutboundWindows(backendStr))}
                                    />
                                </MobileFieldRow>

                                <MobileFieldRow>
                                    <MobileFieldLabel>Retry Allowed Calling Windows</MobileFieldLabel>
                                    <SingleDayTimeRangeField
                                        useCase="outbound"
                                        disabled={!override || !override.timeWindows || override.timeWindows.length === 0}
                                        borderless={true}
                                        value={outboundWindowsToBackendFormat((override && override.timeWindows && override.timeWindows.length > 0) ? override.retryUnconfirmedTimeWindows : [])}
                                        onChange={(backendStr) => handleFieldChange(index, 'retryUnconfirmedTimeWindows', backendFormatToOutboundWindows(backendStr))}
                                    />
                                </MobileFieldRow>
                            </MobileDayCard>
                        );
                    })}
                </MobileCardList>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    padding: 0;
    width: 100%;
    box-sizing: border-box;
    background: transparent;
`;

const TableWrapper = styled.div<{ isForcedTable?: boolean }>`
    width: 100%;
    overflow-x: auto;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background-color: ${color_defaults_white};
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.04);
    display: ${props => props.isForcedTable ? 'block' : 'block'};

    @media (max-width: 1024px) {
        display: ${props => props.isForcedTable ? 'block' : 'none'};
    }
`;

const Table = styled.table`
    width: 100%;
    min-width: 680px;
    border-collapse: collapse;
    font-size: 14px;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #edf2f7;
    background-color: ${color_defaults_white};
    transition: background-color 0.15s ease;
    &:hover {
        background-color: #f8fafc;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const Th = styled.th`
    text-align: left;
    padding: 14px 18px;
    font-weight: 700;
    font-size: 12px;
    color: #4a5568;
    background-color: #f7fafc;
    border-bottom: 2px solid #edf2f7;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    vertical-align: middle;
`;

const Td = styled.td`
    padding: 14px 18px;
    vertical-align: middle;
    .daysAheadInput {
        width: 100%;
        max-width: 80px;
    }
`;

const HeaderLabelWithTooltip = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 100%;
`;

const SundayApplyToAllWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0;
    label {
        font-size: 12px !important;
        font-weight: 600 !important;
        font-family: Muli, sans-serif !important;
        color: #475569 !important;
        white-space: nowrap;
        margin-bottom: 0 !important;
        display: inline-flex !important;
        align-items: center !important;
        position: relative !important;
        padding-left: 20px !important;
        height: 16px !important;
    }
    .check-label {
        font-size: 12px !important;
        font-weight: 600 !important;
        font-family: Muli, sans-serif !important;
        color: #475569 !important;
        line-height: 16px !important;
    }
    .checkmark {
        width: 14px !important;
        height: 14px !important;
        margin-right: 6px !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        &:after {
            left: 4px !important;
            top: 1px !important;
            width: 4px !important;
            height: 8px !important;
        }
    }
`;

const MobileCardList = styled.div<{ isForcedCards?: boolean }>`
    display: ${props => props.isForcedCards ? 'flex' : 'none'};
    flex-direction: column;
    gap: 12px;
    padding: 0;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 1024px) {
        display: flex;
    }
`;

const MobileDayCard = styled.div`
    background-color: ${color_defaults_white};
    border: 1px solid rgba(31, 123, 169, 0.16);
    border-radius: 14px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-shadow: 0 2px 8px rgba(18, 61, 86, 0.04);
    box-sizing: border-box;
    width: 100%;
`;

const MobileDayHeader = styled.div`
    font-size: 15px;
    font-weight: 700;
    font-family: Muli, sans-serif;
    color: #1d3147;
    border-bottom: 1px solid rgba(31, 123, 169, 0.08);
    padding-bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const MobileFieldRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
`;

const MobileInlineFieldRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 12px;
    background: rgba(245, 250, 252, 0.96);
    border: 1px solid rgba(31, 123, 169, 0.12);
    border-radius: 10px;
    box-sizing: border-box;
`;

const MobileFieldLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    font-family: Muli, sans-serif;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const DaysNumberInput = styled.input`
    width: 48px;
    height: 32px;
    text-align: center;
    font-family: Muli, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #1d3147;
    border: 1px solid rgba(31, 123, 169, 0.22);
    border-radius: 8px;
    background: #ffffff;
    padding: 0 4px;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        border-color: #31b6d3;
        box-shadow: 0 0 0 2px rgba(49, 182, 211, 0.15);
    }
`;
